import { Table } from "@/features/Table";
import { putAuthorized } from "@/lib/fetch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type InputFile, input_files_columns } from "./columns";
import { type FilterInputFilesSchema, FilterInputFiles } from "./filter";
import { toast } from "sonner";

const client = "instit-quantilia";
const table_name = "input_files";

const parseGmailLabels = (rawValue: string): string[] =>
  rawValue
    .split(",")
    .map((label) => label.trim().toUpperCase())
    .filter(Boolean);

export const InputFilesTable = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [gmailLabelsRawValue, setGmailLabelsRawValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [clearSelectionAfterSubmit, setClearSelectionAfterSubmit] = useState<
    (() => void) | null
  >(null);

  const isFrench = i18n.language?.startsWith("fr");

  const labels = useMemo(
    () => ({
      bulkEditButton: isFrench
        ? "Modifier les libellés Gmail"
        : "Edit Gmail labels",
      noSelection: isFrench
        ? "Sélectionnez au moins une ligne."
        : "Please select at least one row.",
      bulkEditTitle: isFrench
        ? "Modification en masse des libellés Gmail"
        : "Bulk edit Gmail labels",
      bulkEditDescription: isFrench
        ? "Les libellés saisis seront appliqués à toutes les lignes sélectionnées."
        : "The labels you enter will be applied to all selected rows.",
      selectedCount: isFrench
        ? `${selectedIds.length} ligne(s) sélectionnée(s)`
        : `${selectedIds.length} row(s) selected`,
      inputLabel: isFrench
        ? "Libellés Gmail (séparés par des virgules)"
        : "Gmail labels (comma-separated)",
      applyButton: isFrench ? "Appliquer" : "Apply",
      successToast: (count: number) =>
        isFrench
          ? `Libellés Gmail mis à jour sur ${count} fichier(s).`
          : `Updated Gmail labels on ${count} file(s).`,
      errorToast: isFrench
        ? "Impossible de mettre à jour les libellés Gmail."
        : "Failed to update Gmail labels.",
    }),
    [isFrench, selectedIds.length],
  );

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({
      ids,
      gmailLabels,
    }: {
      ids: number[];
      gmailLabels: string[];
    }) => {
      const updateResults = await Promise.allSettled(
        ids.map((id) =>
          putAuthorized(
            `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/${id}`,
            { gmail_labels: gmailLabels },
          ),
        ),
      );

      const failures = updateResults.filter(
        (result) => result.status === "rejected",
      ).length;

      if (failures > 0) {
        throw new Error(`Failed updates: ${failures}`);
      }

      return ids.length;
    },
    onSuccess: (updatedCount) => {
      toast.success(labels.successToast(updatedCount));
      setIsBulkEditOpen(false);
      setGmailLabelsRawValue("");
      setSelectedIds([]);
      clearSelectionAfterSubmit?.();
      setClearSelectionAfterSubmit(null);
      queryClient.invalidateQueries({ queryKey: [client, table_name] });
    },
    onError: () => {
      toast.error(labels.errorToast);
      queryClient.invalidateQueries({ queryKey: [client, table_name] });
    },
  });

  const openBulkEdit = (rows: InputFile[], clearSelection: () => void) => {
    if (rows.length === 0) {
      toast.error(labels.noSelection);
      return;
    }

    setSelectedIds(rows.map((row) => row.id));
    setClearSelectionAfterSubmit(() => clearSelection);
    setIsBulkEditOpen(true);
  };

  const applyBulkEdit = () => {
    bulkUpdateMutation.mutate({
      ids: selectedIds,
      gmailLabels: parseGmailLabels(gmailLabelsRawValue),
    });
  };

  return (
    <>
      <Table<InputFile, FilterInputFilesSchema>
        label="Input Files"
        client={client}
        table_name={table_name}
        columns={input_files_columns}
        filter={<FilterInputFiles />}
        disable_translations_request={true}
        enableRowSelection={true}
        selectionActions={(selectedRows, clearSelection) =>
          selectedRows.length > 0 ? (
            <Button
              color="secondary"
              variant="flat"
              radius="full"
              size="sm"
              className="capitalize"
              onPress={() => openBulkEdit(selectedRows, clearSelection)}
            >
              {labels.bulkEditButton}
            </Button>
          ) : null
        }
      />

      <Modal
        backdrop="blur"
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
      >
        <ModalContent>
          <ModalHeader>{labels.bulkEditTitle}</ModalHeader>
          <ModalBody className="gap-4">
            <p className="text-sm text-gray-600">
              {labels.bulkEditDescription}
            </p>
            <p className="text-xs text-gray-500">{labels.selectedCount}</p>
            <Input
              label={labels.inputLabel}
              value={gmailLabelsRawValue}
              onValueChange={(value) =>
                setGmailLabelsRawValue(value.toUpperCase())
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="light"
              onPress={() => setIsBulkEditOpen(false)}
            >
              {isFrench ? "Annuler" : "Cancel"}
            </Button>
            <Button
              color="primary"
              isLoading={bulkUpdateMutation.isPending}
              onPress={applyBulkEdit}
            >
              {labels.applyButton}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
